import { useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { parseISO } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CHART_INK, tone, useIsDarkMode } from '@/lib/chartTheme';
import { formatCompact } from '@/lib/money';
import { cn } from '@/lib/utils';
import {
  DashboardChartFilters,
  type DashboardRangeValue,
} from './DashboardFilters';

/** Figma: Visitor yellow, Users blue, Creator pink/red. */
const LEGEND = [
  { key: 'visitors', name: 'Visitor', color: '#facc15' },
  { key: 'users', name: 'Users', color: '#3b82f6' },
  { key: 'creators', name: 'Creator', color: '#fb7185' },
] as const;

type SeriesKey = (typeof LEGEND)[number]['key'];

/** Categories are UTC bucket starts — format in UTC so west-of-UTC locales do not shift a day back. */
const utcMmmD = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  month: 'short',
  day: 'numeric',
});
const utcMmm = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  month: 'short',
});

function cumulative(values: number[]): number[] {
  let sum = 0;
  return values.map((v) => {
    sum += Number(v) || 0;
    return sum;
  });
}

function weekdayInitial(iso: string): string {
  try {
    return ['S', 'M', 'T', 'W', 'T', 'F', 'S'][parseISO(iso).getUTCDay()] ?? '';
  } catch {
    return '';
  }
}

function formatUtcCategoryDate(iso: string, pattern: 'MMM d' | 'MMM'): string {
  const d = parseISO(iso);
  return pattern === 'MMM d' ? utcMmmD.format(d) : utcMmm.format(d);
}

function axisCategoryLabel(iso: string, categoryCount: number): string {
  try {
    if (categoryCount <= 14) return weekdayInitial(iso);
    if (categoryCount <= 40) return formatUtcCategoryDate(iso, 'MMM d');
    return formatUtcCategoryDate(iso, 'MMM');
  } catch {
    return iso;
  }
}

/** Soft gradient fill under each curve (Figma area style). */
function areaGradient(color: string) {
  return {
    type: 'linear' as const,
    x: 0,
    y: 0,
    x2: 0,
    y2: 1,
    colorStops: [
      { offset: 0, color: `${color}55` },
      { offset: 0.55, color: `${color}18` },
      { offset: 1, color: `${color}00` },
    ],
  };
}

/**
 * Figma Platform Growth Overview — smooth area curves with gradient fills.
 */
export function DashboardPlatformGrowthChart({
  categories,
  newSignups,
  newCreators,
  visitorsSeries,
  visitorsComingSoon = true,
  isLoading,
  growthTotal,
  growthDelta,
  range,
  onRangeChange,
  startDate,
  endDate,
  countryCode,
  onCountryCodeChange,
  availableCountryCodes = [],
}: {
  categories: string[];
  newSignups: number[];
  newCreators: number[];
  visitorsSeries?: number[];
  visitorsComingSoon?: boolean;
  isLoading?: boolean;
  growthTotal?: number | null;
  growthDelta?: number | null;
  range: DashboardRangeValue;
  onRangeChange: (value: DashboardRangeValue) => void;
  startDate?: string;
  endDate?: string;
  countryCode: string;
  onCountryCodeChange: (countryCode: string) => void;
  availableCountryCodes?: readonly string[];
}) {
  const isDark = useIsDarkMode();
  const [visible, setVisible] = useState<Record<SeriesKey, boolean>>({
    visitors: !visitorsComingSoon,
    users: true,
    creators: true,
  });

  const option = useMemo(() => {
    const ink = {
      muted: tone(CHART_INK.muted, isDark),
      surface: tone(CHART_INK.surface, isDark),
      primary: tone(CHART_INK.primary, isDark),
      grid: tone(CHART_INK.grid, isDark),
      axis: tone(CHART_INK.axis, isDark),
    };
    const dataByKey: Record<SeriesKey, number[]> = {
      visitors:
        !visitorsComingSoon && visitorsSeries && visitorsSeries.length > 0
          ? cumulative(visitorsSeries)
          : [],
      users: cumulative(newSignups),
      creators: cumulative(newCreators),
    };

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#1e2a44',
        borderColor: 'transparent',
        borderWidth: 0,
        padding: [10, 14],
        textStyle: { color: '#fff', fontSize: 12 },
        axisPointer: {
          type: 'line',
          lineStyle: { color: '#94a3b8', type: 'dashed' },
        },
        formatter: (params: Array<{ seriesName: string; value: number; color: string; axisValue: string; dataIndex: number }>) => {
          if (!Array.isArray(params) || params.length === 0) return '';
          const rows = params
            .map(
              (p) =>
                `<div style="display:flex;justify-content:space-between;gap:16px;margin:2px 0">
                  <span>${p.seriesName === 'Visitor' ? 'Visitors' : p.seriesName === 'Creator' ? 'Creators' : p.seriesName}</span>
                  <span style="font-variant-numeric:tabular-nums">${formatCompact(p.value)}</span>
                </div>`,
            )
            .join('');
          const idx = params[0]?.dataIndex ?? 0;
          const iso = categories[idx];
          let rangeLabel = '';
          if (iso) {
            try {
              rangeLabel = formatUtcCategoryDate(iso, 'MMM d');
            } catch {
              rangeLabel = String(params[0]?.axisValue ?? '');
            }
          }
          return `${rows}<div style="margin-top:8px;opacity:0.7;font-size:11px">${rangeLabel}</div>`;
        },
      },
      grid: { left: 8, right: 16, top: 16, bottom: 8, containLabel: true },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: categories.map((c) => axisCategoryLabel(c, categories.length)),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: ink.muted,
          fontSize: 11,
          hideOverlap: true,
        },
      },
      yAxis: {
        type: 'value',
        min: 0,
        minInterval: 1,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: ink.muted,
          fontSize: 11,
          formatter: (v: number) => formatCompact(v),
        },
        splitLine: {
          show: true,
          lineStyle: { color: ink.grid, type: 'dashed' },
        },
      },
      series: LEGEND.filter((s) => visible[s.key] && dataByKey[s.key].length > 0).map((s) => ({
        name: s.name,
        type: 'line',
        // Figma spline-style curve
        smooth: true,
        showSymbol: false,
        symbol: 'circle',
        symbolSize: 10,
        sampling: 'lttb',
        lineStyle: { width: 2.5, color: s.color },
        itemStyle: {
          color: s.color,
          borderColor: '#fff',
          borderWidth: 2,
        },
        areaStyle: { color: areaGradient(s.color) },
        emphasis: {
          focus: 'series',
          itemStyle: { borderWidth: 3 },
        },
        data: dataByKey[s.key],
      })),
    };
  }, [
    categories,
    isDark,
    newCreators,
    newSignups,
    visible,
    visitorsComingSoon,
    visitorsSeries,
  ]);

  const delta =
    growthDelta != null && Number.isFinite(growthDelta) ? growthDelta : null;
  const rising = (delta ?? 0) >= 0;
  const totalLabel =
    growthTotal != null && Number.isFinite(growthTotal)
      ? formatCompact(growthTotal)
      : null;

  const hasAnySeries =
    visible.users ||
    visible.creators ||
    (visible.visitors && !visitorsComingSoon && (visitorsSeries?.length ?? 0) > 0);

  return (
    <Card className="border-border/40 bg-[#1a1a1c] shadow-none">
      <CardHeader className="space-y-4 p-5 pb-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-2">
            <CardTitle className="text-base font-medium text-foreground">
              Platform Growth Overview
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              {totalLabel ? (
                <p className="text-3xl font-bold tracking-tight tabular-nums">{totalLabel}</p>
              ) : null}
              {delta != null ? (
                <span
                  className={
                    rising
                      ? 'inline-flex items-center gap-0.5 rounded-md bg-emerald-500/15 px-2 py-0.5 text-sm font-medium tabular-nums text-emerald-400'
                      : 'inline-flex items-center gap-0.5 rounded-md bg-rose-500/15 px-2 py-0.5 text-sm font-medium tabular-nums text-rose-400'
                  }
                >
                  {rising ? '+' : ''}
                  {delta.toFixed(1)}%
                  {rising ? (
                    <ArrowUpRight className="size-3.5" aria-hidden />
                  ) : (
                    <ArrowDownRight className="size-3.5" aria-hidden />
                  )}
                </span>
              ) : null}
            </div>
          </div>

          {/* Figma top-right dot legend (still toggles; Visitor shells when PostHog pending). */}
          <div className="flex flex-wrap items-center gap-4 pt-1">
            {LEGEND.map((s) => {
              const comingSoon = s.key === 'visitors' && visitorsComingSoon;
              const on = visible[s.key] && !comingSoon;
              return (
                <button
                  key={s.key}
                  type="button"
                  disabled={comingSoon}
                  title={comingSoon ? 'Visitors pending PostHog' : undefined}
                  className={cn(
                    'inline-flex items-center gap-2 text-xs text-muted-foreground',
                    comingSoon && 'cursor-not-allowed opacity-50',
                    on && 'text-foreground',
                  )}
                  aria-pressed={on}
                  onClick={() => {
                    if (comingSoon) return;
                    setVisible((prev) => ({ ...prev, [s.key]: !prev[s.key] }));
                  }}
                >
                  <span
                    className={cn('size-2.5 rounded-full', !on && !comingSoon && 'opacity-35')}
                    style={{ backgroundColor: s.color }}
                    aria-hidden
                  />
                  {s.name}
                </button>
              );
            })}
          </div>
        </div>

        <DashboardChartFilters
          range={range}
          onRangeChange={onRangeChange}
          countryCode={countryCode}
          onCountryCodeChange={onCountryCodeChange}
          availableCountryCodes={availableCountryCodes}
          dateAriaLabel="Change platform growth date range"
          countryAriaLabel="Change platform growth country filter"
          {...(startDate ? { startDate } : {})}
          {...(endDate ? { endDate } : {})}
        />
      </CardHeader>
      <CardContent className="p-5 pt-2">
        {isLoading ? (
          <div className="flex h-[300px] items-center justify-center text-xs text-muted-foreground">
            Loading growth…
          </div>
        ) : categories.length === 0 || !hasAnySeries ? (
          <div className="flex h-[300px] items-center justify-center rounded-md border border-dashed text-xs text-muted-foreground">
            No growth data for this period.
          </div>
        ) : (
          <ReactECharts
            option={option}
            notMerge
            style={{ height: 300, width: '100%' }}
            className={cn(!hasAnySeries && 'opacity-40')}
          />
        )}
      </CardContent>
    </Card>
  );
}
